/**
 * @file SummaryChips.tsx
 * @description Three small at-a-glance chips on the customer dashboard:
 *   open project count, completed project count, pending-files count.
 *   Server returns the numbers in one GET /me/dashboard call — no
 *   client-side aggregation, no N+1 file fetches.
 * @module Components
 * @security Numbers are scoped to the JWT-derived user_id server-side.
 *   Even if a customer fakes the row in their dev tools, they can't
 *   see another customer's counts.
 */

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getDashboardSummary, type DashboardSummary } from '../../lib/me-api';

export function SummaryChips(): JSX.Element | null {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDashboardSummary()
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide entirely until first load completes — the chips would flash
  // a "0 / 0 / 0" placeholder otherwise. Also hide on error: nothing
  // here is critical, the projects list below still renders.
  if (!loaded || summary === null) return null;
  return (
    <div className="flex flex-wrap gap-3" role="status" aria-label={t('dashboard.summary.aria')}>
      <Chip
        tone="blue"
        hero
        value={summary.open_projects}
        label={t('dashboard.summary.open_projects')}
      />
      <Chip
        tone="green"
        value={summary.completed_projects}
        label={t('dashboard.summary.completed_projects')}
      />
      {summary.pending_files > 0 ? (
        <Link to="#files" className="contents">
          <Chip
            tone="amber"
            value={summary.pending_files}
            label={t('dashboard.summary.pending_files')}
            title={t('dashboard.summary.pending_files_tooltip')}
          />
        </Link>
      ) : null}
      {summary.overdue_projects > 0 ? (
        <Chip
          tone="red"
          value={summary.overdue_projects}
          label={t('dashboard.summary.overdue_projects')}
          title={t('dashboard.summary.overdue_projects_tooltip')}
        />
      ) : null}
    </div>
  );
}

const TONE: Record<'blue' | 'green' | 'amber' | 'red', string> = {
  blue: 'text-blue-700',
  green: 'text-emerald-600',
  amber: 'text-amber-600',
  red: 'text-red-600',
};

interface ChipProps {
  tone: keyof typeof TONE;
  value: number;
  label: string;
  title?: string;
  /** Marks the page's single hero metric (brand-gradient number). */
  hero?: boolean;
}

function Chip({ tone, value, label, title, hero }: ChipProps): JSX.Element {
  return (
    <span className="card px-4 py-3 min-w-[8.5rem] inline-flex flex-col gap-0.5" title={title}>
      <span
        className={`text-2xl font-semibold ${hero === true ? 'text-brand-gradient' : TONE[tone]}`}
      >
        {value}
      </span>
      <span className="text-xs text-slate-500">{label}</span>
    </span>
  );
}
